import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryKey,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import type { AppUser } from '@/lib/auth';
import {
  listPropertiesForUser,
  getPropertyDetailForUser,
  createPropertyForUser,
  updatePropertyForUser,
  deletePropertyForUser,
  type ListPropertiesArgs,
  type PropertyDetailArgs,
} from '@/lib/services/properties';
import {
  createUnitForUser,
  deleteUnitForUser,
  getUnitSummaryForUser,
  updateUnitForUser,
} from '@/lib/services/units';
import { getUnitLeaseContactForUser } from '@/lib/services/unitLeaseContacts';
import { trackPropertyQuickAction } from '@/lib/analytics/events';
import type {
  PaginationInput,
  PropertyDetailResult,
  PropertyFormValues,
  PropertyListFilters,
  PropertyListMetrics,
  PropertyListResult,
  PropertyListSorting,
  PropertySummary,
  UnitFormValues,
  UnitLeaseContact,
  UnitSummary,
} from '@/lib/types/property';
import { createUnitSummaryFromValues, updateUnitSummaryFromForm } from '@/lib/supabase/unitMappers';

type PropertySummaryPatchFields = 'marketingSummary' | 'updatedAt';
type PropertySummaryPatch = Partial<Pick<PropertySummary, PropertySummaryPatchFields>>;
interface UnitUpdateContext {
  previousDetail?: PropertyDetailResult;
}

interface PropertyBasicsVariables {
  propertyId: string;
  values: PropertyFormValues;
}

interface PropertyCreateVariables {
  values: PropertyFormValues;
}

interface PropertyDeleteVariables {
  property: PropertySummary;
}

interface UnitUpdateVariables {
  unit: UnitSummary;
  values: UnitFormValues;
}

interface UnitCreateContext {
  previousDetail?: PropertyDetailResult;
  optimisticId?: string;
}

interface UnitCreateVariables {
  values: UnitFormValues;
}

interface UnitDeleteContext {
  previousDetail?: PropertyDetailResult;
}

interface UnitDeleteVariables {
  unit: UnitSummary;
}

const DEFAULT_PAGINATION: PaginationInput = {
  page: 1,
  pageSize: 50,
};

export interface UseDashboardPropertyListOptions {
  user: AppUser;
  filters?: PropertyListFilters;
  pagination?: PaginationInput;
  sort?: PropertyListSorting;
}

export function useDashboardPropertyList({
  user,
  filters,
  pagination = DEFAULT_PAGINATION,
  sort,
}: UseDashboardPropertyListOptions): UseQueryResult<PropertyListResult> {
  const payload = useMemo<ListPropertiesArgs>(
    () => ({
      user,
      filters,
      pagination,
      sort,
    }),
    [filters, pagination, sort, user],
  );

  const queryKey = useMemo<QueryKey>(() => {
    return [
      'dashboard-properties',
      {
        userId: user.id,
        filters,
        pagination,
        sort,
      },
    ];
  }, [filters, pagination, sort, user.id]);

  return useQuery<PropertyListResult>({
    queryKey,
    queryFn: async () => {
      return await listPropertiesForUser(payload);
    },
    staleTime: 60 * 1000,
    placeholderData: (previous) => previous,
  });
}

export interface UseDashboardPropertyDetailOptions {
  user: AppUser;
  propertyId: string | null;
}

export function useDashboardPropertyDetail({
  user,
  propertyId,
}: UseDashboardPropertyDetailOptions): UseQueryResult<PropertyDetailResult> {
  const payload = useMemo<PropertyDetailArgs | null>(() => {
    if (propertyId == null) {
      return null;
    }
    const trimmedId = propertyId.trim();
    if (trimmedId.length === 0) {
      return null;
    }

    return {
      user,
      propertyId: trimmedId,
    };
  }, [propertyId, user]);

  return useQuery({
    queryKey: ['dashboard-property-detail', { userId: user.id, propertyId }],
    enabled: payload !== null,
    queryFn: async () => {
      if (!payload) {
        throw new Error('Missing property identifier.');
      }

      return await getPropertyDetailForUser(payload);
    },
    staleTime: 60 * 1000,
  });
}

export function createPropertyRowQueryKey(userId: string, propertyId: string): QueryKey {
  return ['dashboard-property-row', { userId, propertyId }];
}

export function useDashboardPropertyRow({
  user,
  property,
}: {
  user: AppUser;
  property: PropertySummary;
}): UseQueryResult<PropertySummary> {
  return useQuery({
    queryKey: createPropertyRowQueryKey(user.id, property.id),
    queryFn: async () => {
      const detail = await getPropertyDetailForUser({ user, propertyId: property.id });
      return detail.summary;
    },
    initialData: property,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function createUnitRowQueryKey(
  userId: string,
  propertyId: string,
  unitId: string,
): QueryKey {
  return ['dashboard-unit-row', { userId, propertyId, unitId }];
}

export function useDashboardUnitRow({
  user,
  propertyId,
  unit,
}: {
  user: AppUser;
  propertyId: string;
  unit: UnitSummary;
}): UseQueryResult<UnitSummary> {
  return useQuery({
    queryKey: createUnitRowQueryKey(user.id, propertyId, unit.id),
    queryFn: async () => {
      return await getUnitSummaryForUser({ user, propertyId, unitId: unit.id });
    },
    initialData: unit,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useDashboardUnitLeaseContact({
  user,
  unitId,
  enabled = true,
}: {
  user: AppUser;
  unitId: string;
  enabled?: boolean;
}): UseQueryResult<UnitLeaseContact | null> {
  return useQuery({
    queryKey: ['dashboard-unit-lease-contact', { userId: user.id, unitId }],
    enabled: enabled && unitId.trim().length > 0,
    queryFn: async () => {
      return await getUnitLeaseContactForUser({ user, unitId });
    },
    staleTime: 60 * 1000,
  });
}

interface PropertyQuickActionContext {
  previousListSnapshots: Array<[QueryKey, PropertyListResult | undefined]>;
  previousDetail?: PropertyDetailResult;
  detailQueryKey: QueryKey;
}

interface PropertyMarketingSummaryVariables {
  property: PropertySummary;
  marketingSummary: string;
}

export function usePropertyMarketingSummary({
  user,
}: {
  user: AppUser;
}): UseMutationResult<
  PropertyDetailResult,
  unknown,
  PropertyMarketingSummaryVariables,
  PropertyQuickActionContext
> {
  const queryClient = useQueryClient();

  return useMutation<
    PropertyDetailResult,
    unknown,
    PropertyMarketingSummaryVariables,
    PropertyQuickActionContext
  >({
    mutationKey: ['dashboard-property-marketing-summary', user.id],
    mutationFn: async ({ property, marketingSummary }) => {
      const detailKey = createDetailQueryKey(user.id, property.id);
      const cachedDetail =
        queryClient.getQueryData<PropertyDetailResult>(detailKey) ??
        (await getPropertyDetailForUser({ user, propertyId: property.id }));

      const trimmedSummary = marketingSummary.trim();

      const values: PropertyFormValues = {
        ...cachedDetail.property,
        marketingSummary: trimmedSummary,
      };

      return await updatePropertyForUser({
        user,
        propertyId: property.id,
        values,
      });
    },
    onMutate: async ({ property, marketingSummary }) => {
      const detailKey = createDetailQueryKey(user.id, property.id);
      await queryClient.cancelQueries({ queryKey: ['dashboard-properties'] });
      await queryClient.cancelQueries({ queryKey: detailKey });

      const previousListSnapshots = queryClient.getQueriesData<PropertyListResult>({
        queryKey: ['dashboard-properties'],
      });
      const previousDetail = queryClient.getQueryData<PropertyDetailResult>(detailKey);

      const updatedAt = new Date().toISOString();
      const trimmedSummary = marketingSummary.trim();
      const patch: PropertySummaryPatch = {
        marketingSummary: trimmedSummary,
        updatedAt,
      };

      const optimisticDetail =
        previousDetail != null
          ? applyPropertySummaryPatch(previousDetail, patch)
          : createOptimisticDetailFromSummary(property, patch);

      writePropertyDetailCache({
        queryClient,
        userId: user.id,
        detail: optimisticDetail,
      });

      const context: PropertyQuickActionContext = {
        previousListSnapshots,
        previousDetail,
        detailQueryKey: detailKey,
      };

      return context;
    },
    onSuccess: (detail) => {
      writePropertyDetailCache({ queryClient, userId: user.id, detail });
      trackPropertyQuickAction({
        propertyId: detail.summary.id,
        propertyCode: detail.summary.propertyCode,
        action: 'marketing_summary_edit',
        status: 'success',
        origin: 'dashboard.properties',
      });
    },
    onError: (error, variables, context) => {
      if (context) {
        context.previousListSnapshots.forEach(([key, data]) => {
          queryClient.setQueryData(key, data);
        });
        queryClient.setQueryData(context.detailQueryKey, context.previousDetail);
      }

      trackPropertyQuickAction({
        propertyId: variables.property.id,
        propertyCode: variables.property.propertyCode,
        action: 'marketing_summary_edit',
        status: 'error',
        origin: 'dashboard.properties',
        errorMessage: error instanceof Error ? error.message : undefined,
      });
    },
    onSettled: async (_result, _error, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: createPropertyRowQueryKey(user.id, variables.property.id),
        }),
        queryClient.invalidateQueries({
          queryKey: createDetailQueryKey(user.id, variables.property.id),
        }),
      ]);
    },
  });
}

export function usePropertyBasicsMutation({
  user,
}: {
  user: AppUser;
}): UseMutationResult<PropertyDetailResult, unknown, PropertyBasicsVariables> {
  const queryClient = useQueryClient();

  return useMutation<PropertyDetailResult, unknown, PropertyBasicsVariables>({
    mutationKey: ['dashboard-property-basics', user.id],
    mutationFn: async ({ propertyId, values }) => {
      return await updatePropertyForUser({
        user,
        propertyId,
        values,
      });
    },
    onSuccess: (detail) => {
      writePropertyDetailCache({ queryClient, userId: user.id, detail });
    },
    onSettled: async (_result, _error, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: createPropertyRowQueryKey(user.id, variables.propertyId),
        }),
        queryClient.invalidateQueries({
          queryKey: createDetailQueryKey(user.id, variables.propertyId),
        }),
      ]);
    },
  });
}

export function usePropertyCreateMutation({
  user,
}: {
  user: AppUser;
}): UseMutationResult<PropertyDetailResult, unknown, PropertyCreateVariables> {
  const queryClient = useQueryClient();

  return useMutation<PropertyDetailResult, unknown, PropertyCreateVariables>({
    mutationKey: ['dashboard-property-create', user.id],
    mutationFn: async ({ values }) => {
      return await createPropertyForUser({ user, values });
    },
    onSuccess: (detail) => {
      writePropertyDetailCache({ queryClient, userId: user.id, detail });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['dashboard-properties'] });
    },
  });
}

export function usePropertyDeleteMutation({
  user,
}: {
  user: AppUser;
}): UseMutationResult<undefined, unknown, PropertyDeleteVariables, PropertyQuickActionContext> {
  const queryClient = useQueryClient();

  return useMutation<undefined, unknown, PropertyDeleteVariables, PropertyQuickActionContext>({
    mutationKey: ['dashboard-property-delete', user.id],
    mutationFn: async ({ property }) => {
      await deletePropertyForUser({ user, propertyId: property.id });
    },
    onMutate: async ({ property }) => {
      await queryClient.cancelQueries({ queryKey: ['dashboard-properties'] });
      const detailQueryKey = createDetailQueryKey(user.id, property.id);
      await queryClient.cancelQueries({ queryKey: detailQueryKey });

      const previousListSnapshots = queryClient.getQueriesData<PropertyListResult>({
        queryKey: ['dashboard-properties'],
      });
      const previousDetail = queryClient.getQueryData<PropertyDetailResult>(detailQueryKey);

      previousListSnapshots.forEach(([key, current]) => {
        if (!current) {
          return;
        }

        const items = current.items.filter((item) => item.id !== property.id);
        const metrics = {
          ...current.metrics,
          total: Math.max(0, current.metrics.total - 1),
          withVacancy: Math.max(0, current.metrics.withVacancy - (property.hasVacancy ? 1 : 0)),
        } satisfies PropertyListMetrics;

        queryClient.setQueryData<PropertyListResult>(key, {
          ...current,
          items,
          totalItems: Math.max(0, current.totalItems - 1),
          totalPages: Math.max(
            1,
            Math.ceil(Math.max(0, current.totalItems - 1) / current.pageSize),
          ),
          metrics,
        });
      });

      queryClient.removeQueries({ queryKey: detailQueryKey, exact: true });

      return {
        previousListSnapshots,
        previousDetail,
        detailQueryKey,
      } satisfies PropertyQuickActionContext;
    },
    onError: (_error, _variables, context) => {
      if (context) {
        context.previousListSnapshots.forEach(([key, data]) => {
          queryClient.setQueryData(key, data);
        });
        queryClient.setQueryData(context.detailQueryKey, context.previousDetail);
      }
    },
    onSettled: async (_result, _error, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['dashboard-properties'] }),
        queryClient.invalidateQueries({
          queryKey: createDetailQueryKey(user.id, variables.property.id),
        }),
      ]);
    },
  });
}

export function useUnitUpdateMutation({
  user,
  propertyId,
}: {
  user: AppUser;
  propertyId: string;
}): UseMutationResult<UnitFormValues, unknown, UnitUpdateVariables, UnitUpdateContext> {
  const queryClient = useQueryClient();
  const detailKey = createDetailQueryKey(user.id, propertyId);

  return useMutation<UnitFormValues, unknown, UnitUpdateVariables, UnitUpdateContext>({
    mutationKey: ['dashboard-unit-update', user.id, propertyId],
    mutationFn: async ({ unit, values }) => {
      const payload: UnitFormValues = {
        ...values,
        id: unit.id,
        propertyId,
      };

      return await updateUnitForUser({
        user,
        propertyId,
        unitId: unit.id,
        values: payload,
      });
    },
    onMutate: async ({ unit, values }) => {
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previousDetail = queryClient.getQueryData<PropertyDetailResult>(detailKey);
      if (previousDetail?.units) {
        const patchedUnits = previousDetail.units.map((entry) => {
          if (entry.id === unit.id) {
            const updated = updateUnitSummaryFromForm(entry, values);
            writeUnitRowCache({
              queryClient,
              userId: user.id,
              propertyId,
              summary: updated,
            });
            return updated;
          }
          return entry;
        });

        queryClient.setQueryData<PropertyDetailResult>(detailKey, {
          ...previousDetail,
          units: patchedUnits,
        });
      } else {
        const updated = updateUnitSummaryFromForm(unit, values);
        writeUnitRowCache({
          queryClient,
          userId: user.id,
          propertyId,
          summary: updated,
        });
      }

      return { previousDetail } satisfies UnitUpdateContext;
    },
    onError: (_error, _variables, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(detailKey, context.previousDetail);
      }
    },
    onSettled: (result, _error, variables) => {
      writeUnitRowCache({
        queryClient,
        userId: user.id,
        propertyId,
        summary: updateUnitSummaryFromForm(variables.unit, variables.values),
      });
      void queryClient.invalidateQueries({ queryKey: detailKey });
      void queryClient.invalidateQueries({
        queryKey: createPropertyRowQueryKey(user.id, propertyId),
      });
      void queryClient.invalidateQueries({
        queryKey: createUnitRowQueryKey(user.id, propertyId, variables.unit.id),
      });
    },
  });
}

export function useUnitCreateMutation({
  user,
  propertyId,
}: {
  user: AppUser;
  propertyId: string;
}): UseMutationResult<UnitFormValues, unknown, UnitCreateVariables, UnitCreateContext> {
  const queryClient = useQueryClient();
  const detailKey = createDetailQueryKey(user.id, propertyId);

  return useMutation<UnitFormValues, unknown, UnitCreateVariables, UnitCreateContext>({
    mutationKey: ['dashboard-unit-create', user.id, propertyId],
    mutationFn: async ({ values }) => {
      const payload: UnitFormValues = {
        ...values,
        propertyId,
      };

      return await createUnitForUser({
        user,
        propertyId,
        values: payload,
      });
    },
    onMutate: async ({ values }) => {
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previousDetail = queryClient.getQueryData<PropertyDetailResult>(detailKey);
      const optimisticId = generateOptimisticId();

      if (previousDetail?.units) {
        const optimisticSummary = createUnitSummaryFromValues(
          { ...values, propertyId },
          { id: optimisticId },
        );

        queryClient.setQueryData<PropertyDetailResult>(detailKey, {
          ...previousDetail,
          units: [...previousDetail.units, optimisticSummary],
        });
        writeUnitRowCache({
          queryClient,
          userId: user.id,
          propertyId,
          summary: optimisticSummary,
        });
      }

      return { previousDetail, optimisticId } satisfies UnitCreateContext;
    },
    onError: (_error, _variables, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(detailKey, context.previousDetail);
      }
    },
    onSuccess: (created, _variables, context) => {
      const detail = queryClient.getQueryData<PropertyDetailResult>(detailKey);
      if (detail?.units) {
        const createdSummary = createUnitSummaryFromValues(created);
        const optimisticId = context.optimisticId;
        const filtered =
          optimisticId != null
            ? detail.units.filter((unit) => unit.id !== optimisticId)
            : detail.units;

        queryClient.setQueryData<PropertyDetailResult>(detailKey, {
          ...detail,
          units: [...filtered, createdSummary],
        });
        writeUnitRowCache({
          queryClient,
          userId: user.id,
          propertyId,
          summary: createdSummary,
        });
      }
    },
    onSettled: (_result, _error, variables, context) => {
      void queryClient.invalidateQueries({ queryKey: detailKey });
      void queryClient.invalidateQueries({
        queryKey: createPropertyRowQueryKey(user.id, propertyId),
      });
      const createdIdFromValues =
        typeof variables.values.id === 'string' ? variables.values.id.trim() : null;
      const optimisticId =
        typeof context?.optimisticId === 'string' ? context.optimisticId.trim() : null;
      const createdId =
        createdIdFromValues !== null && createdIdFromValues.length > 0
          ? createdIdFromValues
          : optimisticId;
      if (typeof createdId === 'string' && createdId.length > 0) {
        void queryClient.invalidateQueries({
          queryKey: createUnitRowQueryKey(user.id, propertyId, createdId),
        });
      }
    },
  });
}

function generateOptimisticId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `temp-${Date.now().toString()}`;
}

export function useUnitDeleteMutation({
  user,
  propertyId,
}: {
  user: AppUser;
  propertyId: string;
}): UseMutationResult<undefined, unknown, UnitDeleteVariables, UnitDeleteContext> {
  const queryClient = useQueryClient();
  const detailKey = createDetailQueryKey(user.id, propertyId);

  return useMutation<undefined, unknown, UnitDeleteVariables, UnitDeleteContext>({
    mutationKey: ['dashboard-unit-delete', user.id, propertyId],
    mutationFn: async ({ unit }) => {
      await deleteUnitForUser({
        user,
        propertyId,
        unitId: unit.id,
      });
    },
    onMutate: async ({ unit }) => {
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previousDetail = queryClient.getQueryData<PropertyDetailResult>(detailKey);

      if (previousDetail?.units) {
        const remaining = previousDetail.units.filter((entry) => entry.id !== unit.id);
        queryClient.setQueryData<PropertyDetailResult>(detailKey, {
          ...previousDetail,
          units: remaining,
        });
      }

      queryClient.removeQueries({
        queryKey: createUnitRowQueryKey(user.id, propertyId, unit.id),
        exact: true,
      });

      return { previousDetail } satisfies UnitDeleteContext;
    },
    onError: (_error, _variables, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(detailKey, context.previousDetail);
      }
    },
    onSettled: (_result, _error, variables) => {
      void queryClient.invalidateQueries({ queryKey: detailKey });
      void queryClient.invalidateQueries({
        queryKey: createPropertyRowQueryKey(user.id, propertyId),
      });
      void queryClient.invalidateQueries({
        queryKey: createUnitRowQueryKey(user.id, propertyId, variables.unit.id),
      });
    },
  });
}

function createDetailQueryKey(userId: string, propertyId: string): QueryKey {
  return ['dashboard-property-detail', { userId, propertyId }];
}

function createOptimisticDetailFromSummary(
  summary: PropertySummary,
  patch?: PropertySummaryPatch,
): PropertyDetailResult {
  const detail: PropertyDetailResult = {
    property: {
      ...summaryToPropertyFormValues(summary),
    },
    summary,
    units: undefined,
    floors: undefined,
  };

  if (!patch) {
    return detail;
  }

  return applyPropertySummaryPatch(detail, patch);
}

function summaryToPropertyFormValues(summary: PropertySummary): PropertyFormValues {
  return {
    id: summary.id,
    propertyCode: summary.propertyCode,
    submarket: summary.submarket,
    assetType: summary.assetType,
    totalRentableSf: summary.totalRentableSf,
    marketingSummary: summary.marketingSummary,
    heroMediaUrls: summary.heroMediaUrls,
    floorPlanMode: summary.floorPlanMode,
    floorPlanImageUrl: summary.floorPlanImageUrl,
    floorPlanConfig: summary.floorPlanConfig,
    address: summary.address,
    createdAt: summary.createdAt,
    updatedAt: summary.updatedAt,
    createdBy: undefined,
    updatedBy: undefined,
  };
}

function applyPropertySummaryPatch(
  detail: PropertyDetailResult,
  patch: PropertySummaryPatch,
): PropertyDetailResult {
  const propertyPatch: Partial<PropertyFormValues> = {};

  if (patch.marketingSummary !== undefined) {
    propertyPatch.marketingSummary = patch.marketingSummary;
  }
  if (patch.updatedAt !== undefined) {
    propertyPatch.updatedAt = patch.updatedAt;
  }

  return {
    ...detail,
    property: {
      ...detail.property,
      ...propertyPatch,
    },
    summary: {
      ...detail.summary,
      ...patch,
    },
  };
}

function writePropertyRowCache({
  queryClient,
  userId,
  summary,
}: {
  queryClient: QueryClient;
  userId: string;
  summary: PropertySummary;
}): void {
  queryClient.setQueryData<PropertySummary>(createPropertyRowQueryKey(userId, summary.id), summary);
}

function writeUnitRowCache({
  queryClient,
  userId,
  propertyId,
  summary,
}: {
  queryClient: QueryClient;
  userId: string;
  propertyId: string;
  summary: UnitSummary;
}): void {
  queryClient.setQueryData<UnitSummary>(
    createUnitRowQueryKey(userId, propertyId, summary.id),
    summary,
  );
}

function writePropertyDetailCache({
  queryClient,
  userId,
  detail,
}: {
  queryClient: QueryClient;
  userId: string;
  detail: PropertyDetailResult;
}): void {
  const detailKey = createDetailQueryKey(userId, detail.summary.id);
  queryClient.setQueryData<PropertyDetailResult>(detailKey, detail);
  writePropertyRowCache({ queryClient, userId, summary: detail.summary });

  queryClient.setQueriesData<PropertyListResult>(
    { queryKey: ['dashboard-properties'] },
    (current) => {
      if (!current) {
        return current;
      }

      const match = current.items.find((item) => item.id === detail.summary.id);
      if (!match) {
        return current;
      }

      const items = current.items.map((item) =>
        item.id === detail.summary.id ? detail.summary : item,
      );

      let metrics = current.metrics;
      if (match.hasVacancy !== detail.summary.hasVacancy) {
        const delta = detail.summary.hasVacancy ? 1 : -1;
        metrics = {
          ...metrics,
          withVacancy: Math.max(0, metrics.withVacancy + delta),
        };
      }

      return {
        ...current,
        items,
        metrics,
      } satisfies PropertyListResult;
    },
  );
}
